import React from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict, isFuture } from "date-fns";
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import { CalendarLink, Card } from "components";
import { Session, SessionTiming } from "types/session";
import { GetCommaSeperatedClientList } from "utils/user";
import { getSessionTiming } from "utils/session";
import { ReactComponent as PhoneImage } from "assets/phone.svg";

interface Props {
  session: Session;
}

const formatSessionTimeString = (timestamp: Date | string): string => {
  const date = new Date(timestamp);
  if (isFuture(date)) {
    return `You have a coaching call starting in ${formatDistanceToNowStrict(
      date
    )}.`;
  }
  return `Your coaching call started ${formatDistanceToNowStrict(date)} ago.`;
};


const ActiveSessionCard: React.FC<Session> = ({
  id,
  title,
  participants,
  start_time,
  coach
}: Session) => (
  <Card w="fit-content" pr="100px" mr="80px">
    <SimpleGrid gap={4}>
      <Heading as="h3" fontWeight="600" fontSize="22px" color="blue.500">
        &ldquo;{title}&rdquo; with {GetCommaSeperatedClientList(participants, coach)}
      </Heading>
      <Text fontSize="17px" color="gray.100">
        {formatSessionTimeString(start_time)}
      </Text>
      <Flex justifyContent="center">
        <Link to={`session/${id}/preview`}>
          <Button variant="blue" size="md">
            Join Call
          </Button>
        </Link>
      </Flex>
    </SimpleGrid>
    <Box w="220px" position="absolute" right="0" top="-10px" mr="-80px">
      <PhoneImage width="100%" />
    </Box>
  </Card>
);

const FutureSessionCard: React.FC<Session> = (session: Session) => {
  const { title, participants, start_time, coach } = session;
  return (
    <Card w="fit-content" pr="100px">
      <SimpleGrid gap={4}>
        <Heading as="h3" fontWeight="600" fontSize="22px" color="blue.500">
          &ldquo;{title}&rdquo; with {GetCommaSeperatedClientList(participants, coach)}
        </Heading>
        <Text fontSize="17px" color="gray.100">
          {formatSessionTimeString(start_time)}
        </Text>
        <CalendarLink session={session} />
      </SimpleGrid>
    </Card>
  );
};

const PastSessionCard: React.FC<Session> = ({ title, participants, coach }: Session) => (
  <Card borderLeftColor="blue.500" borderLeftWidth={8} py={4} px={4} maxW="50%">
    <VStack alignItems="flex-start" spacing={1}>
      <Heading fontSize="22px" fontWeight="600" color="white" mr="80px">
        {title}
      </Heading>
      <Text color="gray.100" fontWeight="300" fontSize="14px">
        {GetCommaSeperatedClientList(participants, coach)}
      </Text>
      <Flex
        pt={2}
        width="100%"
        color="blue.500"
        fontSize="14px"
        justify="space-between"
        align="center"
      >
        <Box>Coaching call</Box>
        {/* <Icon w={8} h={8} as={BsArrowRight} /> */}
      </Flex>
    </VStack>
  </Card>
);

const SessionCard: React.FC<Props> = ({ session }: Props) => {
  switch (getSessionTiming(session)) {
    case SessionTiming.ACTIVE: {
      return <ActiveSessionCard {...session} />;
    }
    case SessionTiming.PAST: {
      return <PastSessionCard {...session} />;
    }
    case SessionTiming.FUTURE: {
      return <FutureSessionCard {...session} />;
    }
    default:
      return <></>;
  }
};

export default SessionCard;
